import { Navigate, useLocation } from "react-router-dom";
import { useAuth, type User } from "@/lib/auth";

type Mode = "guest" | "auth" | "student" | "tutor" | "tutor-onboarding" | "admin";

interface ProtectedRouteProps {
  mode: Mode;
  children: React.ReactNode;
}

function needsOnboarding(user: User): string | null {
  if (user.role === "tutor") {
    if (!user.tutorProfile) return "/onboarding/tutor";
    if (user.tutorApplication?.status === "pending") return "/onboarding/pending";
    // Staff bounced the application back — send the tutor to the form to fix
    // the flagged items and resubmit.
    if (user.tutorApplication?.status === "changes_requested") return "/onboarding/tutor";
    // Rejected is terminal — keep them off the tutor dashboard (which would show
    // a permanent "under review" banner) and on the status page, which renders a
    // distinct rejected state (#44).
    if (user.tutorApplication?.status === "rejected") return "/onboarding/pending";
  }
  if (user.role === "student") {
    if (!user.studentProfile?.studentLanguages?.length) return "/onboarding/student";
  }
  return null;
}

function dashboardPath(user: User) {
  return user.role === "tutor" ? "/tutor/classroom" : "/student/classroom";
}

const ProtectedRoute = ({ mode, children }: ProtectedRouteProps) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (mode === "guest") {
    if (isAuthenticated && user) {
      const onboarding = needsOnboarding(user);
      return <Navigate to={onboarding ?? dashboardPath(user)} replace />;
    }
    return <>{children}</>;
  }

  // All other modes require authentication. Carry the attempted URL through
  // sign-in as ?returnTo= (SignIn already honors it, #216) — without this,
  // email deep links like the approval mail's /tutor/earnings CTA died at the
  // sign-in wall and dumped the user on the default dashboard
  // (§stripe-setup-audit).
  //
  // A sign-out navigates in a transition: the address bar is already on the next
  // page while this one renders once more, with nobody signed in. That page is
  // not one to come back to, so it gets no ?returnTo= (#1346).
  if ((!isAuthenticated || !user) && typeof window !== "undefined" && window.location.pathname !== location.pathname) {
    return null;
  }

  if (!isAuthenticated || !user) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?returnTo=${returnTo}`} replace />;
  }

  // For dashboard modes, redirect to onboarding if not completed
  if (mode === "student" || mode === "tutor") {
    const onboarding = needsOnboarding(user);
    if (onboarding) return <Navigate to={onboarding} replace />;
  }

  // The tutor application is 11 sections long and the server refuses it at the
  // end for a non-tutor ("Not a tutor account"). Turn a student away before
  // they fill it in (#866); the server-side refusal stays the real gate.
  if (mode === "tutor-onboarding" && user.role !== "tutor") {
    return <Navigate to={needsOnboarding(user) ?? dashboardPath(user)} replace />;
  }

  if (mode === "student" && user.role !== "student") {
    return <Navigate to="/tutor/classroom" replace />;
  }

  if (mode === "tutor" && user.role !== "tutor") {
    return <Navigate to="/student/classroom" replace />;
  }

  if (mode === "admin" && user.role !== "admin") {
    return <Navigate to={dashboardPath(user)} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
